import type { Localized, LocalizedList } from "@/i18n/types";

export type Role = {
  company: string;
  client?: string;
  title: Localized;
  /** ISO `YYYY-MM` ranges. `null` end means present. Some roles have two stints. */
  periods: readonly { start: string; end: string | null }[];
  summary: Localized;
  highlights: LocalizedList;
  skills: readonly string[];
};

const SENIOR_ENGINEER: Localized = {
  en: "Senior Software Engineer",
  pt: "Engenheiro de Software Sênior",
};

/** Reverse-chronological by the start of the most recent stint. */
export const experience: readonly Role[] = [
  {
    company: "Lottery Now",
    title: { en: "Lead / Staff Engineer", pt: "Lead / Staff Engineer" },
    periods: [
      { start: "2025-12", end: null },
      { start: "2022-07", end: "2025-03" },
    ],
    summary: {
      en: "Lead engineer across the consumer PWA, the payments wallet, a real-time BFF, a sports-betting product and a hardware ticket scanner.",
      pt: "Engenheiro líder no PWA de consumo, na carteira de pagamentos, em um BFF de tempo real, em um produto de apostas esportivas e em um scanner de bilhetes por hardware.",
    },
    highlights: {
      en: [
        "Led the Mido Lotto PWA (Next.js/TypeScript, XState, Tailwind/styled-components), covering purchase flows for tickets, bets and scratchers, with E2E coverage added to the release path.",
        "Owned the Web Wallet (React, Redux-Saga) across PayNearMe, Venmo, SeamlessChex and Trustly — including monitoring, incident response and feature delivery.",
        "Built and maintained the BFF (NestJS + TypeScript + GraphQL + Postgres + SQS/SNS) fronting multiple microservices under high, elastic load.",
        "Led Mido Bet (Next.js + Redux Toolkit, NestJS + PostgreSQL), integrating a third-party sports-betting provider, with tests, GitHub Actions, Terraform and release practices.",
        "Built ticket-scanner automation (Electron + React + Koffi + OCR + PostgreSQL, state in XState) reading tickets via vendor DLL and routing winners and losers to separate hardware slots.",
        "Drove IaC and CI/CD with Terraform + GitHub Actions to AWS (ECS, RDS, S3, Lambda, API Gateway, Route 53, CloudFront, SQS, SNS, SageMaker), adding pre-release integration tests as a server sanity gate.",
      ],
      pt: [
        "Liderei o PWA Mido Lotto (Next.js/TypeScript, XState, Tailwind/styled-components), cobrindo fluxos de compra de bilhetes, apostas e raspadinhas, com cobertura E2E adicionada ao processo de release.",
        "Fui responsável pela Web Wallet (React, Redux-Saga) integrando PayNearMe, Venmo, SeamlessChex e Trustly — incluindo monitoramento, resposta a incidentes e entrega de features.",
        "Construí e mantive o BFF (NestJS + TypeScript + GraphQL + Postgres + SQS/SNS) na frente de múltiplos microsserviços sob carga alta e elástica.",
        "Liderei o Mido Bet (Next.js + Redux Toolkit, NestJS + PostgreSQL), integrando um provedor terceirizado de apostas esportivas, com testes, GitHub Actions, Terraform e práticas de release.",
        "Construí a automação do scanner de bilhetes (Electron + React + Koffi + OCR + PostgreSQL, estado em XState), lendo bilhetes via DLL do fabricante e encaminhando premiados e não premiados para compartimentos separados.",
        "Conduzi IaC e CI/CD com Terraform + GitHub Actions na AWS (ECS, RDS, S3, Lambda, API Gateway, Route 53, CloudFront, SQS, SNS, SageMaker), adicionando testes de integração pré-release como validação do servidor.",
      ],
    },
    skills: [
      "Next.js",
      "React",
      "TypeScript",
      "NestJS",
      "XState",
      "Redux Toolkit",
      "Redux-Saga",
      "Electron",
      "Koffi",
      "OCR",
      "PostgreSQL",
      "GeoComply",
      "Firebase",
      "Terraform",
      "GitHub Actions",
      "AWS",
      "SageMaker",
    ],
  },
  {
    company: "Nexton",
    client: "Embroker",
    title: SENIOR_ENGINEER,
    periods: [{ start: "2025-03", end: "2025-12" }],
    summary: {
      en: "Client and admin insurance platforms, plus the test infrastructure gating both of them.",
      pt: "Plataformas de seguros para cliente e administração, além da infraestrutura de testes que protege as duas.",
    },
    highlights: {
      en: [
        "Built and maintained a mobile-first React + TypeScript client platform with React Query — new features, RCA on complex bugs, dependency health and vulnerability remediation.",
        "Contributed to a micro-frontend admin combining an Angular shell with React modules; refactored legacy logic and stabilised the integration layers.",
        "Designed automated suites (Jest, React Testing Library, Cypress) and integrated them as critical gates in CI/CD for pull requests and production releases.",
      ],
      pt: [
        "Construí e mantive uma plataforma cliente mobile-first em React + TypeScript com React Query — novas features, análise de causa raiz em bugs complexos, saúde de dependências e correção de vulnerabilidades.",
        "Contribuí com um admin em micro-frontend combinando um shell Angular com módulos React; refatorei lógica legada e estabilizei as camadas de integração.",
        "Desenhei suítes automatizadas (Jest, React Testing Library, Cypress) e as integrei como barreiras críticas no CI/CD para pull requests e releases de produção.",
      ],
    },
    skills: [
      "React",
      "TypeScript",
      "React Query",
      "Angular",
      "Micro-frontends",
      "Jest",
      "Cypress",
      "CI/CD",
    ],
  },
  {
    company: "DevelopPulse",
    title: SENIOR_ENGINEER,
    periods: [{ start: "2020-09", end: "2022-06" }],
    summary: {
      en: "SSR and Jamstack builds for CPA Ontario, The King Center, the Computer History Museum, and a GPS mobile app.",
      pt: "Projetos SSR e Jamstack para CPA Ontario, The King Center, Computer History Museum e um app mobile com GPS.",
    },
    highlights: {
      en: [
        "CPA Ontario (lead): refactored C# + React to Next.js/TypeScript SSR + Agility CMS on Docker/Azure; solved container deploys wiping prerendered content by persisting Next.js artifacts on external volumes; fixed Cloudflare edge routing and caching. Delivered on an aggressive timeline (bonus awarded).",
        "Used Cloudflare Workers to handle proxy and CDN caching.",
        "Stroll: shipped an Ionic/Capacitor app (React/TypeScript/Redux-Saga) for GPS tracking, group participation and progress sharing — iOS and Android.",
        "The King Center (with Microsoft): responsive Next.js SSR front end on headless WordPress, with high-fidelity styling and animation.",
        "Computer History Museum: catalog in Next.js + TypeScript with selective SSR and dynamic filtering.",
      ],
      pt: [
        "CPA Ontario (líder): refatorei C# + React para Next.js/TypeScript com SSR + Agility CMS em Docker/Azure; resolvi o problema de deploys de container apagarem o conteúdo pré-renderizado persistindo os artefatos do Next.js em volumes externos; corrigi o roteamento e o cache de borda no Cloudflare. Entregue em prazo agressivo (com bônus concedido).",
        "Usei Cloudflare Workers para proxy e cache de CDN.",
        "Stroll: publiquei um app em Ionic/Capacitor (React/TypeScript/Redux-Saga) para rastreamento por GPS, participação em grupos e compartilhamento de progresso — iOS e Android.",
        "The King Center (junto à Microsoft): frontend responsivo em Next.js com SSR sobre WordPress headless, com estilização e animações de alta fidelidade.",
        "Computer History Museum: catálogo em Next.js + TypeScript com SSR seletivo e filtros dinâmicos.",
      ],
    },
    skills: [
      "Next.js",
      "TypeScript",
      "React",
      "Redux-Saga",
      "Ionic",
      "Capacitor",
      "Agility CMS",
      "WordPress",
      "Docker",
      "Azure Pipelines",
      "Cloudflare",
      "SSR",
    ],
  },
  {
    company: "BairesDev",
    title: SENIOR_ENGINEER,
    periods: [{ start: "2018-07", end: "2020-04" }],
    summary: {
      en: "Client work across insurance, rodeo events and crypto — frontend, backend and the pipelines behind them.",
      pt: "Projetos para clientes de seguros, eventos de rodeio e cripto — frontend, backend e os pipelines por trás deles.",
    },
    highlights: {
      en: [
        "Praxent — HASC: React, Redux/Redux-Form/Immutable.js, TypeScript, SASS; tests with Jest/RTL/Cypress; active in grooming and technical decisions.",
        "Praxent — WCRA: admin and player apps (React/Redux) with a Node.js/NestJS/Sequelize backend on MySQL; CI/CD with GitHub Actions + Terraform to AWS. Led the refactor to TypeScript + Formik + Immer.",
        "Dapix: React Native + TypeScript + Redux-Saga crypto wallet; stabilised async flows and transaction state.",
        "Regularly called on to unblock other teams by diagnosing and resolving cross-system issues.",
      ],
      pt: [
        "Praxent — HASC: React, Redux/Redux-Form/Immutable.js, TypeScript, SASS; testes com Jest/RTL/Cypress; atuação ativa em grooming e decisões técnicas.",
        "Praxent — WCRA: apps de administração e de jogador (React/Redux) com backend em Node.js/NestJS/Sequelize sobre MySQL; CI/CD com GitHub Actions + Terraform na AWS. Liderei a refatoração para TypeScript + Formik + Immer.",
        "Dapix: carteira cripto em React Native + TypeScript + Redux-Saga; estabilizei fluxos assíncronos e o estado das transações.",
        "Frequentemente acionado para desbloquear outros times, diagnosticando e resolvendo problemas entre sistemas.",
      ],
    },
    skills: [
      "React",
      "Redux",
      "Formik",
      "Immer",
      "React Native",
      "Node.js",
      "NestJS",
      "Sequelize",
      "MySQL",
      "Terraform",
      "AWS",
      "CI/CD",
    ],
  },
  {
    company: "AMcom Information Systems",
    title: SENIOR_ENGINEER,
    periods: [{ start: "2017-11", end: "2018-11" }],
    summary: {
      en: "Healthcare, banking, beverage and agribusiness clients — frontend leadership and framework-level work.",
      pt: "Clientes de saúde, bancos, bebidas e agronegócio — liderança de frontend e trabalho a nível de framework.",
    },
    highlights: {
      en: [
        "Philips (Tasy framework): improved recursive tree logic for schematics; features, fixes and Karma tests; mentored ~15 developers through the migration effort.",
        "Tecne System: led React Native app delivery under tight timelines.",
        "CECRED: frontend lead for loans monitoring (Angular 2+ and SASS).",
        "Ambev: React/Redux and React Native features, with Jest tests.",
        "Bunge: led the frontend for SAP-integrated forms; built a JSON-driven ViewEngine with masks and validations, key to on-time delivery.",
      ],
      pt: [
        "Philips (framework Tasy): melhorei a lógica de árvore recursiva dos esquemáticos; features, correções e testes com Karma; mentorei ~15 desenvolvedores durante a migração.",
        "Tecne System: liderei a entrega de um app em React Native sob prazos apertados.",
        "CECRED: líder de frontend no monitoramento de empréstimos (Angular 2+ e SASS).",
        "Ambev: features em React/Redux e React Native, com testes em Jest.",
        "Bunge: liderei o frontend de formulários integrados ao SAP; construí uma ViewEngine orientada a JSON com máscaras e validações, decisiva para a entrega no prazo.",
      ],
    },
    skills: [
      "Angular",
      "AngularJS",
      "React",
      "Redux",
      "React Native",
      "SASS",
      "Karma",
      "Jest",
      "SAP integration",
      "Mentoring",
    ],
  },
  {
    company: "Linx",
    title: {
      en: "Front End Analyst Developer",
      pt: "Analista Desenvolvedor Front End",
    },
    periods: [{ start: "2017-02", end: "2017-11" }],
    summary: {
      en: "Point-of-sale desktop apps with real peripherals attached.",
      pt: "Aplicações desktop de ponto de venda com periféricos reais acoplados.",
    },
    highlights: {
      en: [
        "Gas Station POS: JavaScript (ES5) + jQuery UI in Electron for Windows, integrating thermal printers and card readers.",
        "JetOil: React/TypeScript + Redux + SASS in Electron, with step-by-step workflows tied to vehicle specs; owned the Node/Webpack/Babel/ESLint tooling.",
      ],
      pt: [
        "PDV de posto de combustível: JavaScript (ES5) + jQuery UI em Electron para Windows, integrando impressoras térmicas e leitores de cartão.",
        "JetOil: React/TypeScript + Redux + SASS em Electron, com fluxos passo a passo ligados às especificações dos veículos; responsável pelo ferramental Node/Webpack/Babel/ESLint.",
      ],
    },
    skills: ["Electron", "JavaScript", "jQuery", "React", "Redux", "SASS", "Webpack", "POS"],
  },
  {
    company: "Philips",
    title: {
      en: "Full Stack Developer",
      pt: "Desenvolvedor Full Stack",
    },
    periods: [{ start: "2016-08", end: "2017-02" }],
    summary: {
      en: "Legacy-to-web migration inside a clinical system.",
      pt: "Migração de legado para web dentro de um sistema clínico.",
    },
    highlights: {
      en: [
        "Migrated Java/Delphi to AngularJS + SASS on the front end with Java on the back; reverse-engineered business rules out of Oracle PL/SQL; worked within the Tasy framework and added Karma/Protractor tests.",
      ],
      pt: [
        "Migrei Java/Delphi para AngularJS + SASS no frontend com Java no backend; fiz engenharia reversa das regras de negócio a partir de Oracle PL/SQL; atuei dentro do framework Tasy e adicionei testes com Karma/Protractor.",
      ],
    },
    skills: ["AngularJS", "SASS", "Java", "Oracle PL/SQL", "Karma", "Protractor"],
  },
  {
    company: "T-Systems Brazil",
    title: {
      en: "Technical Support and Developer",
      pt: "Suporte Técnico e Desenvolvedor",
    },
    periods: [{ start: "2012-04", end: "2016-04" }],
    summary: {
      en: "Incident analysis for Volkswagen, and the tooling to report on it.",
      pt: "Análise de incidentes para a Volkswagen e o ferramental de relatórios em cima disso.",
    },
    highlights: {
      en: [
        "Incident triage and analysis using ITIL v3; built a real-time PHP + jQuery dashboard over Oracle/HP Peregrine for Volkswagen; automated KPI reporting with Excel VBA.",
      ],
      pt: [
        "Triagem e análise de incidentes com ITIL v3; construí um dashboard em tempo real com PHP + jQuery sobre Oracle/HP Peregrine para a Volkswagen; automatizei relatórios de KPI com VBA no Excel.",
      ],
    },
    skills: ["PHP", "jQuery", "Oracle SQL", "HP Peregrine", "ITIL v3", "VBA"],
  },
];
